import { useState } from "react";
import type { SimpleTextConfig } from "../../../../shared/api-contract";
import { useViewer } from "../../../../state/ViewerProvider";
import { useDomainState } from "../../../../state/editor/useEditor";
import { CompactCheck, CompactColor, CompactNumber, CompactSlider, CompactText, MiniBtn, Row } from "../../inspector/rows";


const DEFAULT: SimpleTextConfig = {
  text: "Custom Text", fontSize: 100, width: 1024, height: 1024, xOffset: 0, yOffset: 0,
  boxWidth: 1024, boxHeight: 1024, fontFamily: "", fontPath: "", maskText: false, innerShadow: false,
  textColor: "#000000", bgFillColor: "#ffffff", svgBackground: "#ffffff", transparentBg: false,
  applyToMap: true, applyToBumpMap: false, applyToAlphaMap: false, inverseAlphaMap: false,
};

/** Full Simple-Text decal editor (iJewel SimpleText) — engrave text onto the
 * selected part's material channel(s). */
export function SimpleTextControl() {
  const { client } = useViewer();
  const r = client.ring;
  const groupId = useDomainState("selection") as string | null;
  const [c, setC] = useState<SimpleTextConfig>(DEFAULT);
  const upd = (p: Partial<SimpleTextConfig>) => setC((s) => ({ ...s, ...p }));
  if (!groupId) return <span className="block px-3 py-1.5 text-[11px] text-ed-text-3">Select a part to add a text decal.</span>;
  return (
    <div className="flex flex-col">
      <Row label="Text" stacked><CompactText value={c.text} onChange={(v) => upd({ text: v })} /></Row>
      <Row label="Font Size"><CompactSlider value={c.fontSize} min={2} max={400} step={1} onChange={(v) => upd({ fontSize: v })} /></Row>
      <Row label="Width"><CompactNumber value={c.width} min={2} max={4096} step={1} width={72} onChange={(v) => upd({ width: v })} /></Row>
      <Row label="Height"><CompactNumber value={c.height} min={2} max={4096} step={1} width={72} onChange={(v) => upd({ height: v })} /></Row>
      <Row label="X Offset"><CompactSlider value={c.xOffset} min={-1024} max={1024} step={1} onChange={(v) => upd({ xOffset: v })} /></Row>
      <Row label="Y Offset"><CompactSlider value={c.yOffset} min={-1024} max={1024} step={1} onChange={(v) => upd({ yOffset: v })} /></Row>
      <Row label="Font"><CompactText value={c.fontFamily} placeholder="sans-serif" onChange={(v) => upd({ fontFamily: v })} /></Row>
      <Row label="Font Url"><CompactText value={c.fontPath} onChange={(v) => upd({ fontPath: v })} /></Row>
      <Row label="Mask Text"><CompactCheck checked={c.maskText} onChange={(v) => upd({ maskText: v })} /></Row>
      <Row label="Inner Shadow"><CompactCheck checked={c.innerShadow} onChange={(v) => upd({ innerShadow: v })} /></Row>
      <Row label="Text Color"><CompactColor value={c.textColor} onChange={(v) => upd({ textColor: v })} /></Row>
      <Row label="SVG BG"><CompactColor value={c.svgBackground} onChange={(v) => upd({ svgBackground: v })} /></Row>
      <Row label="Transparent BG"><CompactCheck checked={c.transparentBg} onChange={(v) => upd({ transparentBg: v })} /></Row>
      <Row label="Apply Map"><CompactCheck checked={c.applyToMap} onChange={(v) => upd({ applyToMap: v })} /></Row>
      <Row label="Apply Bump Map"><CompactCheck checked={c.applyToBumpMap} onChange={(v) => upd({ applyToBumpMap: v })} /></Row>
      <Row label="Apply Alpha Map"><CompactCheck checked={c.applyToAlphaMap} onChange={(v) => upd({ applyToAlphaMap: v })} /></Row>
      <Row label="Invert Alpha Map"><CompactCheck checked={c.inverseAlphaMap} onChange={(v) => upd({ inverseAlphaMap: v })} /></Row>
      <div className="px-3 py-1.5"><MiniBtn primary icon="title" onClick={() => void r.applyTextToGroup(groupId, c)}>Add Text</MiniBtn></div>
    </div>
  );
}
